/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import React, { type ReactNode } from 'react';
import {
  Spread,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  SerializedLexicalNode,
  DecoratorNode,
  $applyNodeReplacement,
  NodeKey,
} from 'lexical';
import { PromptItemFragment } from '../../../../../__generated__/graphql';

// const PromptComponent = React.lazy(() => import('./components/LexicalPromptComponent'));
// import PromptComponent from './components/LexicalPromptComponent';

export type SerializedPromptNode = Spread<
  {
    id: string;
    prompt: PromptItemFragment;
  },
  SerializedLexicalNode
>;

function convertPromptElement(domNode: HTMLElement): DOMConversionOutput | null {
  const id = domNode.getAttribute('data-lexical-prompt-id');

  if (id !== null) {
    const prompt = JSON.parse(domNode.getAttribute('data-lexical-prompt-data') || '');
    const node = $createPromptNode(id, prompt);
    return {
      node,
    };
  }

  return null;
}

const TYPE = 'prompt-v1';

export class PromptNode extends DecoratorNode<ReactNode> {
  __id: string;
  __prompt: PromptItemFragment;

  static getType(): string {
    return TYPE;
  }

  static clone(node: PromptNode): PromptNode {
    return new PromptNode(node.__id, node.__prompt, node.__key);
  }

  constructor(id: string, prompt: PromptItemFragment, key?: NodeKey) {
    super(key);
    this.__id = id;
    this.__prompt = prompt;
  }

  isInline(): boolean {
    return true;
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const dom = document.createElement('div');
    dom.className = 'prompt';
    return dom;
  }

  updateDOM(): false {
    return false;
  }

  static importJSON(serializedNode: SerializedPromptNode): PromptNode {
    const node = $createPromptNode(serializedNode.id, serializedNode.prompt);
    return node;
  }

  exportJSON(): SerializedPromptNode {
    return {
      id: this.__id,
      prompt: this.__prompt,
      type: TYPE,
      version: 1,
    };
  }

  getTextContent(
    _includeInert?: boolean | undefined,
    _includeDirectionless?: false | undefined,
  ): string {
    return this.__prompt.emoji + '\n' + this.__prompt.title + '\n' + this.__prompt.content;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('div');
    element.setAttribute('data-lexical-prompt', 'true');
    element.setAttribute('data-lexical-prompt-id', this.__id);
    element.setAttribute('data-lexical-prompt-data', JSON.stringify(this.__prompt));
    const titleNode = document.createElement('p');
    const innerTitleNode = document.createElement('strong');
    innerTitleNode.textContent = `${this.__prompt.emoji}${this.__prompt.title}`;
    titleNode.appendChild(innerTitleNode);
    element.appendChild(titleNode);
    const contentNode = document.createElement('blockquote');
    contentNode.textContent = this.__prompt.content;
    element.appendChild(contentNode);
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-prompt')) {
          return null;
        }
        return {
          conversion: convertPromptElement,
          priority: 1,
        };
      },
    };
  }

  isTextEntity(): true {
    return true;
  }

  canInsertTextBefore(): boolean {
    return false;
  }

  canInsertTextAfter(): boolean {
    return false;
  }

  setRec(prompt: PromptItemFragment): void {
    const self = this.getWritable();
    self.__prompt = prompt;
  }

  decorate(): ReactNode {
    return null;
  }

  // decorate(): ReactNode {
  //   if (typeof window === 'undefined') {
  //     return null;
  //   }

  //   return (
  //     <PromptComponent
  //       id={this.__id}
  //       prompt={this.__prompt}
  //       nodeKey={this.__key}
  //       updatePrompt={(prompt) => {
  //         this.setRec(prompt);
  //       }}
  //     />
  //   );
  // }
}

export function $createPromptNode(id: string, prompt: PromptItemFragment): PromptNode {
  return new PromptNode(id, prompt);
}

export function $isPromptNode(node: LexicalNode | null | undefined): node is PromptNode {
  return node instanceof PromptNode;
}
